import { useQuery } from '@apollo/client';

import { Card } from '@ovComponents/2-component/card/card';
import { UserContext } from '@providers/userContextProvider';
import type { Theme } from 'hephaestus/core';
import { Box, IconButton, Tooltip, Typography, useTheme } from 'hephaestus/web';
import {
  Close as CloseIcon,
  ErrorOutlined as ErrorOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  Lightbulb as LightbulbIcon,
  WarningAmber as WarningAmberIcon,
} from 'hephaestus/webIcons';
import {
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { FETCH_UPCOMING_TRANSFERS } from '../queries';
import { LABEL_SX, SECONDARY_TEXT_SX, SMALL_SX } from '../styles';
import { useTradingHub } from '../tradingHubContext';
import { formatRelativeTime } from '../utils/relativeTimeI18n';

type Insight = {
  id: string;
  type: 'alert' | 'suggestion' | 'info' | 'warning';
  title: string;
  description: string;
  action?: string;
  onAction?: () => void;
};

const InsightRule = ({
  insight,
  theme,
}: {
  insight: Insight;
  theme: Theme;
}) => {
  const iconColors: Record<Insight['type'], string> = {
    alert: theme.color.negative,
    warning: theme.color.warning,
    suggestion: theme.color.primary,
    info: theme.color.onSurfaceVariant,
  };
  const borderColors: Record<Insight['type'], string> = {
    alert: theme.color.negative,
    warning: theme.color.warning,
    suggestion: theme.color.primary,
    info: theme.color.outlineVariant,
  };
  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: '3px solid',
        borderLeftColor: borderColors[insight.type],
        p: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
        }}
      >
        {insight.type === 'alert' && (
          <ErrorOutlineIcon sx={{ fontSize: 14, color: iconColors.alert }} />
        )}
        {insight.type === 'warning' && (
          <WarningAmberIcon sx={{ fontSize: 14, color: iconColors.warning }} />
        )}
        {insight.type === 'suggestion' && (
          <LightbulbIcon sx={{ fontSize: 14, color: iconColors.suggestion }} />
        )}
        {insight.type === 'info' && (
          <InfoOutlinedIcon sx={{ fontSize: 14, color: iconColors.info }} />
        )}
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Typography
            variant='bodySmall'
            component='div'
            sx={{ fontWeight: 700, mb: 0.25 }}
          >
            {insight.title}
          </Typography>
          <Typography
            variant='bodySmall'
            component='div'
            sx={SECONDARY_TEXT_SX}
          >
            {insight.description}
          </Typography>
          {insight.action && insight.onAction && (
            <Typography
              variant='bodySmall'
              component='div'
              sx={{
                color: theme.color.primary,
                cursor: 'pointer',
                fontWeight: 600,
                mt: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={insight.onAction}
            >
              {insight.action}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const InsightSidebar = () => {
  const formatMoney = useFormatMoney();
  const theme = useTheme();
  const { t } = useTranslation('portfolioTradingHub');
  const {
    statusBarStats,
    setInsightSidebarOpen,
    setCurrentTab,
    setPortfolioStatusPreFilter,
    setTradeStatusPreFilter,
    setOperationsPreFilter,
  } = useTradingHub();
  const { activeOrganization } = useContext(UserContext);
  const [lastUpdated] = useState(() => new Date());

  const { data: upcomingData } = useQuery(FETCH_UPCOMING_TRANSFERS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          perPage: 100,
          sortField: 'scheduledDate',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
    fetchPolicy: 'cache-first',
  });

  const cashFlowInsights = useMemo(() => {
    const transfers =
      upcomingData?.fetchUpcomingTransfers?.upcomingTransfers || [];
    if (transfers.length === 0)
      return {
        totalCount: 0,
        outflowCount: 0,
        inflowCount: 0,
        imminentCount: 0,
        atRiskCount: 0,
        totalOutflowCents: 0,
      };
    const now = Date.now();
    let outflowCount = 0,
      inflowCount = 0,
      imminentCount = 0,
      atRiskCount = 0,
      totalOutflowCents = 0;
    transfers.forEach((t: any) => {
      const isOutflow = t.type === 'WITHDRAW' || t.type === 'withdraw';
      if (isOutflow) {
        outflowCount++;
        totalOutflowCents += t.amountCents || 0;
        const mvCents = t.subAccount?.statistics?.marketValueCents || 0;
        if (t.amountCents > mvCents * 0.9) atRiskCount++;
      } else inflowCount++;
      if (t.scheduledDate) {
        const diffDays = Math.ceil(
          (new Date(t.scheduledDate).getTime() - now) / 86_400_000,
        );
        if (diffDays <= 1) imminentCount++;
      }
    });
    return {
      totalCount: transfers.length,
      outflowCount,
      inflowCount,
      imminentCount,
      atRiskCount,
      totalOutflowCents,
    };
  }, [upcomingData]);

  const navigateTo = useCallback(
    (
      tab: string,
      preFilter?: { portfolio?: string; trade?: string; operations?: string },
    ) => {
      setInsightSidebarOpen(false);
      startTransition(() => {
        if (preFilter?.portfolio)
          setPortfolioStatusPreFilter(preFilter.portfolio);
        if (preFilter?.trade) setTradeStatusPreFilter(preFilter.trade);
        if (preFilter?.operations) setOperationsPreFilter(preFilter.operations);
        setCurrentTab(tab as any);
      });
    },
    [
      setCurrentTab,
      setInsightSidebarOpen,
      setPortfolioStatusPreFilter,
      setTradeStatusPreFilter,
      setOperationsPreFilter,
    ],
  );

  const totalPortfolios =
    statusBarStats.needsAction +
    statusBarStats.inProgress +
    statusBarStats.onTrack;

  const ruleBasedInsights: Insight[] = useMemo(() => {
    const rules: Insight[] = [];
    if (statusBarStats.needsAction > 0) {
      const urgencyPct =
        totalPortfolios > 0
          ? Math.round((statusBarStats.needsAction / totalPortfolios) * 100)
          : 0;
      rules.push({
        id: 'drift-alert',
        type: 'alert',
        title: t('insights.driftAlert.title', {
          count: statusBarStats.needsAction,
        }),
        description: t('insights.driftAlert.description', {
          percent: urgencyPct,
        }),
        action: t('insights.driftAlert.action'),
        onAction: () =>
          navigateTo('portfolios', { portfolio: 'ANY_REBALANCE' }),
      });
    }
    if (statusBarStats.activeOperations > 0) {
      rules.push({
        id: 'ops-active',
        type: 'warning',
        title: t('insights.opsActive.title', {
          count: statusBarStats.activeOperations,
        }),
        description: t('insights.opsActive.description'),
        action: t('insights.opsActive.action'),
        onAction: () => navigateTo('operations', { operations: 'ACTIVE' }),
      });
    }
    if (statusBarStats.tradesInFlight > 0) {
      const isBulk = statusBarStats.tradesInFlight > 50;
      rules.push({
        id: 'trades-flight',
        type: isBulk ? 'warning' : 'info',
        title: t('insights.tradesInFlight.title', {
          count: statusBarStats.tradesInFlight,
        }),
        description: isBulk
          ? t('insights.tradesInFlight.descriptionBulk', {
              count: statusBarStats.tradesInFlight,
            })
          : t('insights.tradesInFlight.descriptionDefault'),
        action: t('insights.tradesInFlight.action'),
        onAction: () => navigateTo('tradeRequests', { trade: 'IN_FLIGHT' }),
      });
    }
    if (cashFlowInsights.atRiskCount > 0) {
      rules.push({
        id: 'cash-flow-risk',
        type: 'alert',
        title: t('insights.cashFlowRisk.title', {
          count: cashFlowInsights.atRiskCount,
        }),
        description: t('insights.cashFlowRisk.description'),
        action: t('insights.cashFlowRisk.action'),
        onAction: () => navigateTo('today'),
      });
    }
    if (cashFlowInsights.imminentCount > 0) {
      rules.push({
        id: 'cash-flow-imminent',
        type: 'warning',
        title: t('insights.cashFlowImminent.title', {
          count: cashFlowInsights.imminentCount,
        }),
        description: t('insights.cashFlowImminent.description'),
        action: t('insights.cashFlowImminent.action'),
        onAction: () => navigateTo('today'),
      });
    }
    if (
      cashFlowInsights.totalCount > 0 &&
      cashFlowInsights.atRiskCount === 0 &&
      cashFlowInsights.imminentCount === 0
    ) {
      rules.push({
        id: 'cash-flow-overview',
        type: 'info',
        title: t('insights.cashFlowOverview.title', {
          count: cashFlowInsights.totalCount,
        }),
        description: t('insights.cashFlowOverview.description', {
          inflows: t('insights.cashFlowOverview.descriptionInflows', {
            count: cashFlowInsights.inflowCount,
          }),
          outflows: t('insights.cashFlowOverview.descriptionOutflows', {
            count: cashFlowInsights.outflowCount,
          }),
          outflowAmount: formatMoney(cashFlowInsights.totalOutflowCents),
        }),
        action: t('insights.cashFlowOverview.action'),
        onAction: () => navigateTo('today'),
      });
    }
    if (
      totalPortfolios > 0 &&
      statusBarStats.needsAction === 0 &&
      statusBarStats.activeOperations === 0 &&
      statusBarStats.tradesInFlight === 0
    ) {
      rules.push({
        id: 'all-clear',
        type: 'info',
        title: t('insights.allClear.title'),
        description: t('insights.allClear.description', {
          count: statusBarStats.onTrack,
        }),
      });
    }
    if (
      statusBarStats.totalAum > 0 &&
      statusBarStats.activeOperations === 0 &&
      statusBarStats.needsAction > 3
    ) {
      rules.push({
        id: 'bulk-optimize',
        type: 'suggestion',
        title: t('insights.bulkOptimize.title'),
        description: t('insights.bulkOptimize.description', {
          count: statusBarStats.needsAction,
        }),
        action: t('insights.bulkOptimize.action'),
        onAction: () =>
          navigateTo('portfolios', { portfolio: 'ANY_REBALANCE' }),
      });
    }
    if (
      statusBarStats.tradesInFlight > 0 &&
      statusBarStats.activeOperations === 0
    ) {
      rules.push({
        id: 'settlement-check',
        type: 'suggestion',
        title: t('insights.settlementCheck.title'),
        description: t('insights.settlementCheck.description'),
        action: t('insights.settlementCheck.action'),
        onAction: () => navigateTo('tradeRequests', { trade: 'IN_FLIGHT' }),
      });
    }
    return rules;
  }, [
    statusBarStats,
    totalPortfolios,
    cashFlowInsights,
    navigateTo,
    t,
    formatMoney,
  ]);

  const insights = ruleBasedInsights;
  const alertCount = insights.filter(
    (i) => i.type === 'alert' || i.type === 'warning',
  ).length;
  const suggestionCount = insights.filter(
    (i) => i.type === 'suggestion',
  ).length;
  const infoCount = insights.filter((i) => i.type === 'info').length;
  const showSections = insights.length >= 4;

  const timeAgo = useMemo(
    () => formatRelativeTime(lastUpdated.toISOString(), t),
    [lastUpdated, t],
  );

  const SECTIONS = [
    {
      key: 'alerts',
      label: t('insights.sections.alerts'),
      count: alertCount,
      items: insights.filter((i) => i.type === 'alert' || i.type === 'warning'),
    },
    {
      key: 'suggestions',
      label: t('insights.sections.suggestions'),
      count: suggestionCount,
      items: insights.filter((i) => i.type === 'suggestion'),
    },
    {
      key: 'info',
      label: t('insights.sections.info'),
      count: infoCount,
      items: insights.filter((i) => i.type === 'info'),
    },
  ];

  const alerts = SECTIONS[0].items;

  const renderSectionHeader = (label: string, count: number) =>
    count > 0 && showSections ? (
      <Typography
        variant='labelSmall'
        component='div'
        sx={{
          ...SMALL_SX,
          color: theme.color.onDisabled,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          mt: 1,
          mb: 0.5,
          px: 0.5,
        }}
      >
        {label} ({count})
      </Typography>
    ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: theme.color.background,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: theme.color.outlineVariant,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <LightbulbIcon sx={{ fontSize: 16, color: theme.color.warning }} />
          <Typography variant='titleMedium'>{t('insights.title')}</Typography>
          {alertCount > 0 && (
            <Box
              sx={{
                minWidth: 18,
                height: 18,
                borderRadius: '9px',
                bgcolor: alerts.some((i) => i.type === 'alert')
                  ? theme.color.negative
                  : theme.color.warning,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
              }}
            >
              <Typography
                variant='bodySmall'
                sx={{
                  ...LABEL_SX,
                  color: theme.color.onPrimary,
                  fontWeight: 700,
                }}
              >
                {alertCount}
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title={t('insights.closeA11y')}>
          <IconButton
            size='xsmall'
            onClick={() => setInsightSidebarOpen(false)}
            aria-label={t('insights.closeA11y')}
          >
            <CloseIcon sx={{ fontSize: 16 }} fontSize='inherit' />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1.25 }}>
        {SECTIONS.map(({ key, label, count, items }) => (
          <Box key={key}>
            {renderSectionHeader(label, count)}
            {items.map((i) => (
              <InsightRule key={i.id} insight={i} theme={theme} />
            ))}
          </Box>
        ))}
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: '1px solid',
            borderColor: theme.color.outlineVariant,
            textAlign: 'center',
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_SX, color: theme.color.onDisabled }}
          >
            {t('insights.footer.poweredBy')}
            {' \u2022 '}
            {t('relativeTime.updated', { time: timeAgo })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InsightSidebar;
